/**
 * WayAI permission types and auth context definitions.
 *
 * Permissions follow the pattern `resource:action`.
 * Grants use scope-based matching with OR semantics for hub_tags.
 *
 * User types and org requirements:
 * - Platform Admin: needs org, has `platform:admin` permission
 * - Org Owner/Admin: needs org, org-scoped grants with `org:admin`
 * - Hub Admin: org-scoped, has `hub:admin` on specific hubs
 * - Hub Team User: NO org needed, hub-scoped grants only
 * - Hub End User: NO org needed, hub-scoped grants only
 */

export const PERMISSIONS = [
  'hub:read',
  'hub:write',
  'hub:admin',
  'conversation:read',
  'conversation:write',
  'analytics:read',
  'billing:read',
  'billing:write',
  'org:read',
  'org:write',
  'org:admin',
  'contacts:write',
  'platform:admin',
  '*',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** All user-level permissions — excludes `'*'` (superuser) and `'platform:admin'`. */
export const USER_PERMISSIONS: Permission[] = [
  'hub:read',
  'hub:write',
  'hub:admin',
  'conversation:read',
  'conversation:write',
  'analytics:read',
  'billing:read',
  'billing:write',
  'org:read',
  'org:write',
  'org:admin',
  'contacts:write',
];

/** Hub-admin-only permissions — for users who admin a hub but are NOT org members. */
export const HUB_ADMIN_PERMISSIONS: Permission[] = [
  'hub:read',
  'hub:write',
  'hub:admin',
  'conversation:read',
  'conversation:write',
  'analytics:read',
];

export interface Grant {
  scope: {
    /** Organization this grant applies to. undefined = unscoped (hub-only users). */
    org_id?: string;
    /** Hub IDs this grant applies to. ['*'] = all hubs in the org. */
    hubs: string[];
    /**
     * Hub tags for tag-based scoping (replaces project grouping).
     * OR matching: user has access if hub has ANY of these tags.
     * To require multiple tags, use separate grants.
     */
    hub_tags?: string[];
    /** Environment filter. undefined = all environments. */
    environments?: ('production' | 'preview')[];
  };
  permissions: Permission[];
}

export interface AuthContext {
  /** Platform org ID. Optional — hub-only users (end users, team users) don't have one. */
  org_id?: string;
  /** WorkOS user ID. Present for human sessions, absent for service tokens. */
  user_id?: string;
  /** User email. Present for human sessions (set during auth key generation). */
  email?: string;
  /** WorkOS session id (sid). Present for sessions minted from an authenticated browser/CLI login. */
  session_id?: string;
  /** Scoped API token id. Present only when caller authenticated with a `way_` token. */
  token_id?: string;
  /** Parent token expiry (ISO 8601). Present only when the `way_` parent has a finite lifetime; used to cap child expiries. */
  token_expires_at?: string;
  /**
   * True only for a synthetic harness token (harness-agents PR 2.6): a privileged
   * internal `way_` token minted for an in-sandbox agent, whose `user_id` is a
   * deterministic synthetic UUID with NO backing UserDO profile. This flag — NOT
   * the sentinel email — is the sole discriminant `isHarnessToken()` reads, so a
   * real user can never gain harness treatment by owning a look-alike email, and
   * a harness principal never triggers phantom UserDO provisioning.
   */
  is_harness?: boolean;
  grants: Grant[];
}

/**
 * Parse the comma-separated PLATFORM_ADMIN_EMAILS env var into a deduplicated
 * lowercase list. Shared by `checkPlatformAdminEmail` and live-update
 * recipient resolution so the membership boundary can't drift between them.
 */
export function parsePlatformAdminEmails(raw: string | undefined): string[] {
  if (!raw) return [];
  return Array.from(
    new Set(raw.split(',').map((e) => e.trim().toLowerCase()).filter(Boolean)),
  );
}

/**
 * Check if an email matches PLATFORM_ADMIN_EMAILS env var.
 * Case-insensitive, filters empty entries from trailing commas.
 * Returns false if either argument is falsy.
 */
export function checkPlatformAdminEmail(email: string | undefined, platformAdminEmails: string | undefined): boolean {
  if (!email || !platformAdminEmails) return false;
  return parsePlatformAdminEmails(platformAdminEmails).includes(email.toLowerCase());
}
